'use client';
import UserAgentContext, { ConfigContext } from './UserAgentContext.jsx';
import { NextIntlClientProvider } from 'next-intl';
import Header from './Header/Header.jsx';
import english from '../../messages/en-US.json';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { useEffect, useMemo, useState } from 'react';
import { fetchCall } from '@/utils/fetchUtils.js';
import { LoadingProgress } from 'swp-react';
import { useRouter } from 'next/router.js';
import { usePathname } from 'next/navigation.js';

import Script from 'next/script.js';

import {
	createLogger,
	aiPageView,
	logToAi,
	clearQueue,
} from '@/utils/logging.js';
import { useDispatch, useSelector } from 'react-redux';
import {
	selectFailedImages,
	selectShowInvalidFilesModal,
	selectUnsupportedFiles,
	setFailedImages,
	setShowInvalidFilesModal,
	setUnsupportedFiles,
} from '@/slices/uploadSlice.jsx';
import InvalidFilesModal from './Modals/InvalidFilesModal/InvalidFilesModal.jsx';
import { selectRetailerId, selectUserId } from '@/slices/commonSlice.jsx';
import { getUploadDetails } from '@/utils/uploadUtils.js';

const allMessages = {
	de: () =>
		import('../../messages/de-DE.json').then((module) => module.default),
	en: () =>
		import('../../messages/en-US.json').then((module) => module.default),
	es: () =>
		import('../../messages/es-ES.json').then((module) => module.default),
	fr: () =>
		import('../../messages/fr-FR.json').then((module) => module.default),
};

const getAllMessages = async (locale) => allMessages[locale]();

const valid = (obj) => obj !== null && obj !== undefined;

const ClientLayout = ({ children }) => {
	// Hooks
	const pathname = usePathname();
	const dispatch = useDispatch();
	// State
	const [isLoading, setIsLoading] = useState(true);
	/**
	 * @type {
	 *   ReturnType<typeof useState<{
	 *   	 aiConnectionString: string,
	 *   	 enableTransfer: string,
	 *       enableFaceDetection: string,
	 *   	 faceDetectionThreshold: string,
	 *   	 acceptedFileTypes: string,
	 *   	 device: { vendor: string, model: string },
	 *   	 os: { name: string },
	 *   	 browser: { name: string },
	 *   	 locale: string,
	 *   	 facebookClientId: string,
	 *   }>>
	 * } The environment variable data returned by the api/env endpoint.
	 **/
	const [data, setData] = useState(false);
	const [messageObject, setMessageObject] = useState(english);
	const [showHeader, setShowHeader] = useState();
	// Selectors
	const unsupportedFiles = useSelector(selectUnsupportedFiles);
	const userId = useSelector(selectUserId);
	const showInvalidFilesModal = useSelector(selectShowInvalidFilesModal);
	const retailerId = useSelector(selectRetailerId);
	const failedImages = useSelector(selectFailedImages);
	// const knumber = useSelector(selectKnumber);

	useEffect(() => {
		fetchCall('/api/env')
			.then(async (res) => {
				try {
					const messages = await getAllMessages(res.locale);
					setMessageObject(messages);
				} catch (err) {
					console.error('something went wrong getting locale', err);
				}
				setIsLoading(false);
				setData(res);
				try {
					window.fbAsyncInit = function () {
						FB.init({
							appId: res.facebookClientId,
							cookie: true,
							xfbml: true,
							version: 'v20.0',
						});
					};

					// setTimeout(() => {
					const scriptTag = document.createElement('script');
					scriptTag.setAttribute(
						'src',
						'https://connect.facebook.net/en_US/sdk.js'
					);
					document.body.appendChild(scriptTag);
				} catch (err) {
					console.error(
						'something went wrong getting facebook client id',
						err
					);
				}

				if (valid(res?.aiConnectionString)) {
					createLogger(res.aiConnectionString);
				}
			})
			.catch((err) => {
				setIsLoading(false);
			});
	}, []);

	useEffect(() => {
		if (unsupportedFiles?.length > 0 || failedImages?.length > 0) {
			dispatch(setShowInvalidFilesModal(true));
		}
	}, [unsupportedFiles, failedImages?.length]);

	useEffect(() => {
		if (pathname === '/facebook') {
			setShowHeader(false);
		} else {
			setShowHeader(true);
		}

		aiPageView(pathname);
	}, [pathname]);

	useEffect(() => {
		if (userId && retailerId) {
			dispatch(getUploadDetails(userId));
		}
	}, [userId, retailerId]);

	useEffect(() => {
		setTimeout(() => {
			clearQueue();
		}, 5000);
	}, []);

	const acceptedFileTypes = useMemo(() => {
		const v =
			data?.acceptedFileTypes ||
			'image/jpeg,image/png,image/heic,image/heif,image/avif,image/bmp';
		return v;
	}, [data]);

	const enableTransfer = useMemo(() => {
		const v = data?.enableTransfer || 'true';
		return v;
	}, [data]);

	/** @type {boolean} */
	const enableFaceDetection = useMemo(() => {
		const v = data?.enableFaceDetection === 'true' || true;
		return v;
	}, [data]);

	/** @type {number | null} */
	const faceDetectionThreshold = useMemo(() => {
		try {
			const v = parseFloat(data?.faceDetectionThreshold);
			return v;
		} catch (err) {
			console.error('Error parsing faceDetectionThreshold:', err);
			return null;
		}
	}, [data]);

	const userAgent = useMemo(() => {
		const obj = {
			os: data?.os || { name: 'IOS' },
			device: data?.device || { vendor: 'Apple', model: 'iPhone' },
			browser: data?.browser || { name: 'Safari' },
		};
		return obj;
	}, [data]);

	if (isLoading && !data) {
		return (
			<div
				style={{
					height: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
				className="layout-loading-spinner"
			>
				<LoadingProgress />
			</div>
		);
	}

	// TODO: create a context provider to pass in accepted fileTypes and the environment variable to switch
	// cloud transfer on/off and any other value that we need to use commonly across different components
	return (
		<NextIntlClientProvider
			locale={data?.locale || 'en'}
			messages={messageObject}
		>
			<ConfigContext.Provider
				value={{
					acceptedFileTypes,
					enableCloudTransfer: enableTransfer,
					enableFaceDetection: enableFaceDetection,
					faceDetectionThreshold: faceDetectionThreshold,
				}}
			>
				<UserAgentContext.Provider value={userAgent}>
					<div className="body-container">
						{showHeader && <Header />}
						<div className="content-container">{children}</div>
						{showInvalidFilesModal && (
							<InvalidFilesModal
								unsupportedFiles={unsupportedFiles}
								failedImages={failedImages}
								handleCloseModal={() => {
									dispatch(setUnsupportedFiles([]));
									dispatch(setShowInvalidFilesModal(false));
									dispatch(setFailedImages([]));
								}}
								open={showInvalidFilesModal}
							/>
						)}
					</div>
				</UserAgentContext.Provider>
			</ConfigContext.Provider>
		</NextIntlClientProvider>
	);
};

export default ClientLayout;
